import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { MemberDayException, MemberExceptionType } from '@/lib/clinicHours';

export interface ClinicMemberScheduleException {
  id: string;
  membershipId: string;
  userId?: string;
  exceptionType: MemberExceptionType;
  startsOn: string;
  endsOn: string;
  startsTime: string | null;
  endsTime: string | null;
}

export const memberScheduleExceptionsQueryKey = (
  clinicId: string | null | undefined,
  from: string | null,
  to: string | null,
) => ['member-schedule-exceptions', clinicId ?? null, from, to] as const;

function hhmm(value: string | null | undefined): string | null {
  return value ? value.slice(0, 5) : null;
}

export function toMemberDayExceptions(
  rows: ClinicMemberScheduleException[] | undefined,
  membershipId: string | undefined,
  isoDate: string,
): MemberDayException[] {
  return (rows ?? [])
    .filter(
      (row) =>
        row.membershipId === membershipId && row.startsOn <= isoDate && row.endsOn >= isoDate,
    )
    .map((row) => ({
      type: row.exceptionType,
      startsTime: row.startsTime,
      endsTime: row.endsTime,
    }));
}

export function useMemberScheduleExceptions(
  clinicId: string | null | undefined,
  from: string | null,
  to: string | null,
) {
  return useQuery({
    queryKey: memberScheduleExceptionsQueryKey(clinicId, from, to),
    enabled: Boolean(clinicId && from && to),
    queryFn: async (): Promise<ClinicMemberScheduleException[]> => {
      if (!clinicId || !from || !to) return [];
      const { data, error } = await supabase
        .from('member_schedule_exceptions')
        .select('id, membership_id, exception_type, starts_on, ends_on, starts_time, ends_time')
        .eq('clinic_id', clinicId)
        .lte('starts_on', to)
        .gte('ends_on', from);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: String(row.id),
        membershipId: String(row.membership_id),
        exceptionType: row.exception_type as MemberExceptionType,
        startsOn: String(row.starts_on),
        endsOn: String(row.ends_on),
        startsTime: hhmm(row.starts_time as string | null),
        endsTime: hhmm(row.ends_time as string | null),
      }));
    },
    staleTime: 0,
  });
}

export function useUpsertPractitionerDayHours(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      practitionerIds: string[];
      date: string;
      action: 'weekly' | 'closed' | 'custom_hours';
      start?: string | null;
      end?: string | null;
    }): Promise<number> => {
      if (!clinicId) throw new Error('No active clinic');
      if (input.practitionerIds.length === 0) {
        throw new Error('practitioner ids required');
      }
      const { data, error } = await supabase.rpc(
        'upsert_practitioner_day_hours' as never,
        {
          p_clinic_id: clinicId,
          p_practitioner_ids: input.practitionerIds,
          p_date: input.date,
          p_action: input.action,
          p_start: input.start ?? undefined,
          p_end: input.end ?? undefined,
        } as never,
      );
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member-schedule-exceptions'] });
      void qc.invalidateQueries({ queryKey: ['clinic-members'] });
      void qc.invalidateQueries({ queryKey: ['public-booking'] });
      void qc.invalidateQueries({ queryKey: ['clinic-hours-appointment-count'] });
    },
  });
}
